import React, { useMemo } from 'react';
import { Spin, Select, Button as AntButton } from 'antd';
import { Container } from '@/components/ui/Container';
import { useUserDetail } from '@hooks/users/useUserDetail';
import { useRoles } from '@hooks/rbac/useRoles';
import { useUpdateUser } from '@hooks/users/useUpdateUser';
import { DetailLabel, DetailValue, DetailBadge, DetailPanelLoading, DetailValueInherit } from './style';

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleString();
    } catch {
        return dateStr;
    }
};

/**
 * Content for the user side panel. Use inside <SidePanel>.
 */
const UserDetailPanel = ({ userId }) => {
    const { data: user, isLoading, isError } = useUserDetail(userId);
    const { data: roles = [] } = useRoles();
    const updateUserMutation = useUpdateUser();

    const roleOptions = useMemo(
        () => roles.map((r) => ({ value: r.id, label: r.name })),
        [roles],
    );

    const currentRoleId = useMemo(
        () => (Array.isArray(user?.roles) && user.roles.length > 0 ? user.roles[0].id : undefined),
        [user],
    );

    const handleStatusChange = (nextStatus) => {
        if (!userId) return;
        updateUserMutation.mutate({ id: userId, body: { status: nextStatus } });
    };

    const handleRoleChange = (nextRoleId) => {
        if (!userId) return;
        updateUserMutation.mutate({ id: userId, body: { roleId: nextRoleId } });
    };

    if (!userId) return null;
    if (isLoading) {
        return (
            <DetailPanelLoading>
                <Spin size="large" />
            </DetailPanelLoading>
        );
    }
    if (isError || !user) {
        return (
            <Container $marginBottom="24px">
                <DetailValueInherit>Failed to load user details.</DetailValueInherit>
            </Container>
        );
    }

    const plan = user.plan;

    return (
        <>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Email :</DetailLabel>
                <DetailValue>{user.email ?? '—'}</DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Status :</DetailLabel>
                <DetailValue>
                    <Select
                        size="small"
                        style={{ minWidth: 140 }}
                        value={['active', 'suspended', 'deactivated'].includes(user.status) ? user.status : 'active'}
                        onChange={handleStatusChange}
                        disabled={updateUserMutation.isPending}
                        options={[
                            { value: 'active', label: 'Active' },
                            { value: 'suspended', label: 'Suspended' },
                            { value: 'deactivated', label: 'Deactivated' },
                        ]}
                    />
                </DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Plan :</DetailLabel>
                <DetailValue>{plan?.name ?? '—'}</DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Roles :</DetailLabel>
                <DetailValue>
                    <Select
                        size="small"
                        style={{ minWidth: 180 }}
                        value={currentRoleId}
                        onChange={handleRoleChange}
                        disabled={updateUserMutation.isPending}
                        options={roleOptions}
                        placeholder="Select role"
                    />
                </DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Last login :</DetailLabel>
                <DetailValue>{formatDate(user.lastLogin)}</DetailValue>
            </Container>
            <Container $marginBottom="24px" $display="flex" $alignItems="flex-start" $gap="12px">
                <DetailLabel>Created at :</DetailLabel>
                <DetailValue>{formatDate(user.createdAt)}</DetailValue>
            </Container>
        </>
    );
};

export default UserDetailPanel;
